import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useHasProfile } from '@/features/profile/hooks/useHasProfile';
import { readPendingOnboarding, ONBOARDING_PATH } from '@/features/onboarding/pendingOnboarding';

const LoadingScreen: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#160C0C] text-white">
    <div className="text-lg font-medium">Loading...</div>
  </div>
);

/**
 * Guards the authenticated app: needs a user AND a profile.
 *
 * - Not logged in                → /
 * - Logged in, no profile        → /complete-profile
 * - Profile check errored        → renders the page (fails open)
 */
export const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  const { data: hasProfile, isLoading: profileLoading } = useHasProfile();
  const location = useLocation();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  // Still resolving whether the user has a profile.
  if (profileLoading) {
    return <LoadingScreen />;
  }

  // Only redirect when we positively know there is no profile. If the check
  // errored (hasProfile === undefined after loading), fail open and render the
  // page rather than stranding a real user on /complete-profile.
  if (hasProfile === false) {
    return <Navigate to="/complete-profile" replace />;
  }

  // The profile exists but the onboarding it started was never finished — send
  // the user back instead of leaving them in the app with no name, belt or
  // academy. Cleared by the onboarding itself on completion.
  const pendingOnboarding = readPendingOnboarding();
  if (pendingOnboarding && location.pathname !== ONBOARDING_PATH) {
    return <Navigate to={ONBOARDING_PATH} replace state={pendingOnboarding} />;
  }

  return <>{children}</>;
};

// Guards /complete-profile: needs a user, but NOT a profile (that's the point).
export const CompleteProfileRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  const { data: hasProfile, isLoading: profileLoading } = useHasProfile();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  if (profileLoading) {
    return <LoadingScreen />;
  }

  // Already has a profile — nothing to complete.
  if (hasProfile === true) {
    return <Navigate to="/home" replace />;
  }

  return <>{children}</>;
};
